import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Box,
  type BoxProps,
  Flex,
  Skeleton,
  Text,
} from '@mantine/core'
import Link from 'next/link'

import { useTranslation } from '@flecto/translations'
import { type CoreItem } from '@flecto/types'
import { AvatarItem, Icon } from '@flecto/ui'

import { useAccessibilityProps } from '../../hooks/useAccessibilityProps'
import { useStoreAnalytics } from '../../hooks/useStoreAnalytics'
import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDates } from '../../hooks/useStoreDates'
import { useStoreItemAvailability } from '../../hooks/useStoreItemAvailability'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'
import { InputItemQuantity } from '../InputItemQuantity/InputItemQuantity'
import { TextCurrencyStore } from '../TextCurrencyStore/TextCurrencyStore'
import styles from './styles.module.css'

export type RowItemProps = BoxProps & {
  item?: CoreItem
}

export function RowItem({ item }: RowItemProps) {
  if (!item) {
    return
  }

  const accessibilityProps = useAccessibilityProps({
    type: 'item',
    content: item?.title ?? '',
  })
  const { t } = useTranslation('store', { keyPrefix: 'store-shared' })

  const path = useStorePathBuilder()

  const { startAt, endAt } = useStoreDates()

  const { removeItem, getBasketItem } = useStoreBasket()

  const { items } = useStoreBasket()

  const { track } = useStoreAnalytics()

  const basketItem = getBasketItem(item)

  const { item: itemWithAvailability, isLoading: isLoadingItemAvailability } =
    useStoreItemAvailability({ item, quantityToAdd: basketItem?.quantity })

  return (
    <Box className={styles.item}>
      <Link href={path(`/items/${item.id}`)} className={styles.avatar}>
        <AvatarItem
          h={55}
          w={55}
          item={item as unknown as CoreItem}
          {...accessibilityProps}
        />
      </Link>
      <Text
        fw={500}
        size='sm'
        truncate
        component={Link}
        href={path(`/items/${item.id}`)}
      >
        {itemWithAvailability?.title}
      </Text>
      {isLoadingItemAvailability ? (
        <Skeleton h={10} w={100} mt={3} mb={2} />
      ) : (
        <Text c='dimmed' size='xs' className={styles.availability}>
          {t(itemWithAvailability.availabilityLabel)}
        </Text>
      )}
      {isLoadingItemAvailability ? (
        <Skeleton h={10} w={50} mt={3} mb={2} />
      ) : startAt && endAt ? (
        <TextCurrencyStore
          fw={500}
          size='sm'
          cents={itemWithAvailability.suggestedPrice}
          className={styles.price}
          labelFallback='&nbsp;'
        />
      ) : (
        ''
      )}
      <Flex className={styles.quantity} gap='xs' ml='xs'>
        <ActionIcon
          size='xs'
          variant='light'
          onClick={() => {
            removeItem(item)

            track('UPDATE__BASKET', {
              item_ids: items.map((i) => i.item.id),
              type: 'remove',
            })
          }}
        >
          <Icon icon={faTrashCan} size='xs' />
        </ActionIcon>
        <InputItemQuantity item={item} size='xs' w={80} />
      </Flex>
    </Box>
  )
}
